import { Component, OnInit } from '@angular/core';
import { CompositeProductInfoModel, HxCountryService, HxProductInfoService, HxTagService, HxUiLabelPipe, PagedList, uiLabel } from 'hx-services';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HxPagedListDirective } from 'hx-components';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import {
  NzFilterTriggerComponent,
  NzTableCellDirective,
  NzTableComponent,
  NzTbodyComponent,
  NzThAddOnComponent,
  NzTheadComponent,
  NzThMeasureDirective,
  NzTrDirective
} from 'ng-zorro-antd/table';
import { NgFor } from '@angular/common';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzCheckboxGroupComponent } from 'ng-zorro-antd/checkbox';
import { FormsModule } from '@angular/forms';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';

interface DataItem extends CompositeProductInfoModel {
  subProductTitles?: string;
  countryTitle?: string;
}

interface ListFilter {
  name: string;
  options: { label: string, value: string | number, checked?: boolean }[];
  isVisible: boolean;
  isActive: boolean;
  operator: 'and' | 'or';
}
// hello
@Component({
    selector: 'hc-composite-products-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzButtonComponent, RouterLink, NzIconDirective, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzFilterTriggerComponent, NzTbodyComponent, NgFor, NzButtonGroupComponent, NzWaveDirective, NzPopconfirmDirective, NzDropdownMenuComponent, NzCheckboxGroupComponent, FormsModule, NzRadioGroupComponent, NzRadioComponent, HxUiLabelPipe, TranslocoPipe]
})
export class CompositeProductsListComponent extends HxPagedListDirective<DataItem> implements OnInit {
  countryFilter: ListFilter = {
    options: [], isActive: false, isVisible: false, operator: 'or', name: 'countryIds'
  };
  tagsFilter: ListFilter = {
    options: [], isActive: false, isVisible: false, operator: 'or', name: 'tags'
  };

  constructor(
    override router: Router,
    override aRoute: ActivatedRoute,
    private productInfoService: HxProductInfoService,
    private tr: TranslocoService,
    private tagService: HxTagService,
    private countryService: HxCountryService,
  ) {
    super(router, aRoute);
  }

  ngOnInit() {
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        this.isTableLoading = true;
        if (this.countryFilter.options.length === 0) {
          const countryIds: number[] = [];
          const tagNames: string[] = [];
          if (paramMap.get('country_ids')) {
            (paramMap.get('country_ids') as 'string')
              .split(',')
              .map(s => Number(s))
              .forEach(id => countryIds.push(id));
          } else if (paramMap.get('tags')) {
            (paramMap.get('tags') as 'string')
              .split(',')
              .forEach(tag => tagNames.push(tag));
          } else if (paramMap.get('stores_op')) {
            this.countryFilter.operator = paramMap.get('stores_op') === 'and' ? 'and' : 'or';
          }
          Promise.all([
            this.countryService.getCountries(),
            this.tagService.getTags('CompositeProduct'),
          ]).then(([countries, tags]) => {
            this.countryFilter.options = countries.map(country => {
              return {label: uiLabel(this.tr.getActiveLang(), country.title) ?? '', value: country.id, checked: countryIds.includes(country.id)};
            });
            this.tagsFilter.options = tags.map(tag => {
              return {label: tag, value: tag, checked: tagNames.includes(tag)};
            });
            this.countryFilter.isActive = this.countryFilter.options.some(op => op.checked);
            this.tagsFilter.isActive = this.tagsFilter.options.some(op => op.checked);
            super.load(paramMap);
          });
        } else {
          this.countryFilter.isActive = this.countryFilter.options.some(op => op.checked);
          this.tagsFilter.isActive = this.tagsFilter.options.some(op => op.checked);
          super.load(paramMap);
        }
      });
  }

  resetFilter(filter: ListFilter) {
    filter.options.forEach(op => op.checked = false);
  }

  applyFilter(filter: ListFilter) {
    const checkedArr = filter.options.filter(v => v.checked).map(v => v.value);
    let filterParam: string | null = null;
    let filterOp: 'and' | 'or' | null = null;
    if (checkedArr.length !== 0) {
      filterParam = checkedArr.join(',');
      filterOp = filter.operator;
    }
    const queryParams: Params = {};
    queryParams[filter.name] = filterParam;
    queryParams[filter.name + '_op'] = filterOp;
    filter.isVisible = false;
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  protected loadList(): Promise<PagedList<DataItem>> {
    const countryIds = this.countryFilter.options.filter(op => op.checked).map(op => Number(op.value));
    const tags = this.tagsFilter.options.filter(op => op.checked).map(op => String(op.value));
    return firstValueFrom(this.productInfoService.getCompositeProducts({
      page: this.page,
      limit: this.limit,
      // language: this.language,
      sort: this.getSortParams(),
      countryIds: countryIds,
      storesOp: this.countryFilter.operator,
      tags: tags,
      tagsOp: this.tagsFilter.operator,
    })).then(products => {
      const list: DataItem[] = products.list.map(cp => {
        const dataItem: DataItem = cp;
        if (dataItem.subProducts) {
          dataItem.subProductTitles = dataItem.subProducts.map(sp => uiLabel(this.tr.getActiveLang(), sp.title)).join(', ');
        }
        if (dataItem.country) {
          dataItem.countryTitle = uiLabel(this.tr.getActiveLang(), dataItem.country.title);
        }
        return dataItem;
      });
      return {count: products.count, list: list};
    });
  }

  remove(el: CompositeProductInfoModel) {
    this.productInfoService.removeCompositeProduct(el.id)
      .subscribe(() => this.router.navigate([]));
  }
}
